import React, { useState, useEffect } from 'react';
import { getRoles, createRole, getPermissions, Role, Permission } from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { Plus, Shield, Eye, X, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import PermissionDeniedPage from '../../pages/PermissionDeniedPage';

const RolesList: React.FC = () => {
  const { hasAnyPermission } = useArtist();
  const { userProfile } = useUser();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [createForm, setCreateForm] = useState({
    roleCode: '',
    roleName: '',
    description: '',
    roleLevel: 0,
    permissions: [] as string[],
  });

  // Get systemRole from sessionStorage
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null;
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();
  const isSystemAdmin = systemRole === "system_admin";

  // Only system admins can access roles page
  if (!isSystemAdmin) {
    return <PermissionDeniedPage />;
  }

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRoles(false);
      setRoles(data);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      setError(err.message || 'Failed to fetch roles');
      toast.error(err.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const data = await getPermissions();
      setPermissions(data);
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      toast.error('Failed to fetch permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const fetchRoleWithPermissions = async (roleId: number) => {
    try {
      const data = await getRoles(true);
      const role = data.find((r) => r.roleId === roleId);
      if (role) {
        setRoles((prev) =>
          prev.map((r) => (r.roleId === roleId ? { ...r, permissions: role.permissions } : r))
        );
      }
    } catch (err: any) {
      console.error('Error fetching role permissions:', err);
      toast.error('Failed to fetch role permissions');
    }
  };

  const toggleRoleExpansion = (roleId: number) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
      // Fetch permissions if not already loaded
      const role = roles.find((r) => r.roleId === roleId);
      if (role && !role.permissions) {
        fetchRoleWithPermissions(roleId);
      }
    }
    setExpandedRoles(newExpanded);
  };

  const handleCreateRole = async () => {
    if (!createForm.roleCode.trim() || !createForm.roleName.trim()) {
      toast.error('Role Code and Role Name are required');
      return;
    }

    // Validate role code format (should be lowercase with underscores)
    const roleCodePattern = /^[a-z][a-z0-9_]*$/;
    if (!roleCodePattern.test(createForm.roleCode)) {
      toast.error('Role code must be lowercase, start with a letter, and contain only letters, numbers, and underscores');
      return;
    }

    try {
      await createRole({
        roleCode: createForm.roleCode.trim(),
        roleName: createForm.roleName.trim(),
        description: createForm.description.trim() || undefined,
        roleLevel: createForm.roleLevel,
        permissions: createForm.permissions,
      });
      toast.success('Role created successfully');
      setShowCreateDialog(false);
      setCreateForm({
        roleCode: '',
        roleName: '',
        description: '',
        roleLevel: 0,
        permissions: [],
      });
      fetchRoles();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create role');
    }
  };

  const togglePermission = (permissionCode: string) => {
    setCreateForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionCode)
        ? prev.permissions.filter((p) => p !== permissionCode)
        : [...prev.permissions, permissionCode],
    }));
  };

  const toggleCategoryPermissions = (category: string) => {
    const categoryPerms = permissions[category] || [];
    const categoryPermCodes = categoryPerms.map((p) => p.permissionCode);
    const allSelected = categoryPermCodes.every((code) => createForm.permissions.includes(code));

    setCreateForm((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !categoryPermCodes.includes(p))
        : [...new Set([...prev.permissions, ...categoryPermCodes])],
    }));
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Roles & Permissions
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Roles & Permissions
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mt-[3rem] mb-6">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">
            Roles & Permissions
          </span>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
          >
            <Plus size={16} />
            <span>Create Custom Role</span>
          </button>
        </div>

        {/* Roles Table */}
        <div className="mb-6 sm:mb-8 mt-[2rem]">
          {roles.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="text-gray-600 font-family-messina-sans mb-4">No roles found</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Role
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Code
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Level
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Type
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <React.Fragment key={role.roleId}>
                        <tr className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="text-[0.875rem] text-gray-900 font-family-messina-sans font-medium">
                              {role.roleName}
                            </div>
                            {role.description && (
                              <div className="text-xs text-gray-500 font-family-messina-sans mt-1">
                                {role.description}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            <code className="px-2 py-1 bg-gray-100 rounded text-xs">{role.roleCode}</code>
                          </td>
                          <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                            {role.roleLevel}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded text-xs font-family-messina-sans ${
                                role.isSystemRole
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}
                            >
                              {role.isSystemRole ? 'System' : 'Custom'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded text-xs font-family-messina-sans ${
                                role.isActive !== false
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {role.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => toggleRoleExpansion(role.roleId)}
                              className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors text-sm font-family-messina-sans"
                              title="View permissions"
                            >
                              <Eye size={16} />
                              <span>View Permissions</span>
                              {expandedRoles.has(role.roleId) ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </button>
                          </td>
                        </tr>
                        {expandedRoles.has(role.roleId) && (
                          <tr>
                            <td colSpan={6} className="px-4 py-4 bg-gray-50">
                              {role.permissions ? (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3 font-family-messina-sans">
                                    Permissions ({role.permissions.length})
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {role.permissions.map((perm) => (
                                      <span
                                        key={perm}
                                        className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-family-messina-sans"
                                      >
                                        {perm}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 font-family-messina-sans">
                                  Loading permissions...
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Role Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Create Custom Role
              </h3>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setCreateForm({
                    roleCode: '',
                    roleName: '',
                    description: '',
                    roleLevel: 0,
                    permissions: [],
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                    Role Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.roleCode}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, roleCode: e.target.value.toLowerCase().replace(/\s+/g, '_') })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                    placeholder="custom_role_code"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 font-family-messina-sans">
                    Lowercase, letters, numbers, and underscores only
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.roleName}
                    onChange={(e) => setCreateForm({ ...createForm, roleName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                    placeholder="Custom Role Name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                  placeholder="Role description"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Role Level
                </label>
                <input
                  type="number"
                  value={createForm.roleLevel}
                  onChange={(e) => setCreateForm({ ...createForm, roleLevel: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                  placeholder="0"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1 font-family-messina-sans">
                  Higher numbers indicate more privileges (0-100)
                </p>
              </div>

              {/* Permissions Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-family-messina-sans">
                  Permissions ({createForm.permissions.length} selected)
                </label>
                {loadingPermissions ? (
                  <div className="text-sm text-gray-500">Loading permissions...</div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {Object.entries(permissions).map(([category, categoryPerms]) => (
                      <div key={category} className="border-b border-gray-200 last:border-b-0 pb-3 last:pb-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900 font-family-messina-sans capitalize">
                            {category}
                          </h4>
                          <button
                            onClick={() => toggleCategoryPermissions(category)}
                            className="text-xs text-gray-600 hover:text-gray-900 font-family-messina-sans"
                          >
                            {categoryPerms.every((p) => createForm.permissions.includes(p.permissionCode))
                              ? 'Deselect All'
                              : 'Select All'}
                          </button>
                        </div>
                        <div className="space-y-2 ml-4">
                          {categoryPerms.map((permission) => (
                            <label
                              key={permission.permissionId}
                              className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={createForm.permissions.includes(permission.permissionCode)}
                                onChange={() => togglePermission(permission.permissionCode)}
                                className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="text-sm text-gray-900 font-family-messina-sans">
                                  {permission.permissionName}
                                </div>
                                <div className="text-xs text-gray-500 font-family-messina-sans">
                                  {permission.permissionCode}
                                </div>
                                {permission.description && (
                                  <div className="text-xs text-gray-400 font-family-messina-sans mt-1">
                                    {permission.description}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setCreateForm({
                    roleCode: '',
                    roleName: '',
                    description: '',
                    roleLevel: 0,
                    permissions: [],
                  });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
              >
                <Save size={16} />
                <span>Create Role</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesList;

